/**
 * What a tech typed into a block's Log to Autotask form: its ticket title and
 * notes (#218; ADR 0097, which supersedes ADR 0084).
 *
 * - **Here first, as the tech types**, in this browser's memory: the form
 *   unmounts when its block turns In progress and back, on Change client, and
 *   on another day, and the typing comes back with it while any of it isn't
 *   saved yet. Once it's all saved, the form starts from the server's draft,
 *   which may be newer (the engine merged another block's notes in, or another
 *   device typed); the cached day is kept in step with each save (`drafts.tsx`).
 * - **Saved on the block** (`PUT /api/v1/blocks/{id}/draft`) a second after
 *   typing stops, and at once when a field is left, so a reload or another
 *   device starts from it. One save at a time for each block, always the
 *   latest, so two saves never race. The saving lives here, not in the form,
 *   so a save waiting when the form unmounts still goes.
 * - **A failed save loses nothing:** the typing stays here, the form says so
 *   quietly, and the next pause or field left tries again.
 * - **Never in localStorage or sessionStorage:** notes are free text about
 *   possibly HIPAA clients.
 * - **Cleared once the block is logged.** The server clears its copy when the
 *   submission is recorded.
 */
import { createContext, useContext, useState } from "react";

export interface Draft {
  /** Null: nothing typed, so the form shows its suggestion. "" is a field emptied. */
  title: string | null;
  notes: string | null;
}

/** Saves a block's draft on the server; rejects if it wasn't saved. */
export type SaveDraft = (blockId: string, draft: Draft) => Promise<unknown>;

/** How long after the last key a draft is saved. */
export const DRAFT_SAVE_DELAY_MS = 1_000;

const NONE: Draft = { title: null, notes: null };

function same(a: Draft, b: Draft): boolean {
  return a.title === b.title && a.notes === b.notes;
}

interface Entry {
  /** What the form says: the tech's latest typing. */
  draft: Draft;
  /** What the server has, as far as this browser knows. */
  saved: Draft;
  timer: ReturnType<typeof setTimeout> | null;
  saving: boolean;
  /** The last save failed: the typing is only here. */
  failed: boolean;
}

/** Typing the server doesn't have yet: waiting for its pause, being saved, or not saved. */
function unsaved(entry: Entry): boolean {
  return entry.timer !== null || entry.saving || entry.failed || !same(entry.draft, entry.saved);
}

export class DraftStore {
  private readonly entries = new Map<string, Entry>();
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly save: SaveDraft | null = null,
    private readonly delay: number = DRAFT_SAVE_DELAY_MS,
  ) {}

  /**
   * The form's starting draft: this session's typing while some of it isn't
   * saved yet, or else the server's. Once it's all saved, the server's may be
   * newer (the engine merged another block's notes in, or another device
   * typed), and the next key must build on that, not on this older copy.
   */
  start(blockId: string, server: Draft): Draft {
    const entry = this.entries.get(blockId);
    if (entry && unsaved(entry)) {
      return entry.draft;
    }
    if (entry) {
      entry.draft = server;
      entry.saved = server;
    } else {
      this.entries.set(blockId, { draft: server, saved: server, timer: null, saving: false, failed: false });
    }
    return server;
  }

  get(blockId: string): Draft {
    return this.entries.get(blockId)?.draft ?? NONE;
  }

  /**
   * What the block's draft says now, changing nothing: this session's typing
   * while some of it isn't saved yet, or else the server's, as `start` would
   * begin from. For a card's peek (ADR 0193), with its form closed.
   */
  latest(blockId: string, server: Draft): Draft {
    const entry = this.entries.get(blockId);
    return entry && unsaved(entry) ? entry.draft : server;
  }

  /** What the tech typed: kept now, saved once typing stops. */
  set(blockId: string, changes: Partial<Draft>): void {
    const entry = this.entry(blockId);
    entry.draft = { ...entry.draft, ...changes };
    this.stopTimer(entry);
    entry.timer = setTimeout(() => {
      entry.timer = null;
      this.run(blockId, entry);
    }, this.delay);
  }

  /** Save now what's waiting: a field was left. */
  flush(blockId: string): void {
    const entry = this.entries.get(blockId);
    if (entry) {
      this.stopTimer(entry);
      this.run(blockId, entry);
    }
  }

  /** Whether the block's last save failed, so its typing is only in this browser. */
  failed(blockId: string): boolean {
    return this.entries.get(blockId)?.failed ?? false;
  }

  /** The block is logged: forget its draft, and send nothing more for it. */
  clear(blockId: string): void {
    const entry = this.entries.get(blockId);
    if (entry) {
      this.stopTimer(entry);
      this.entries.delete(blockId);
      this.emit();
    }
  }

  /** For useSyncExternalStore: told when a block's `failed` changes. */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private entry(blockId: string): Entry {
    let entry = this.entries.get(blockId);
    if (!entry) {
      entry = { draft: NONE, saved: NONE, timer: null, saving: false, failed: false };
      this.entries.set(blockId, entry);
    }
    return entry;
  }

  private stopTimer(entry: Entry): void {
    if (entry.timer !== null) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
  }

  private run(blockId: string, entry: Entry): void {
    if (this.save === null || entry.saving || same(entry.draft, entry.saved)) {
      return;
    }
    const sent = entry.draft;
    entry.saving = true;
    this.save(blockId, sent).then(
      () => {
        entry.saved = sent;
        this.settle(blockId, entry, false);
      },
      () => {
        this.settle(blockId, entry, true);
      },
    );
  }

  private settle(blockId: string, entry: Entry, failed: boolean): void {
    entry.saving = false;
    if (this.entries.get(blockId) !== entry) {
      return; // logged meanwhile
    }
    if (entry.failed !== failed) {
      entry.failed = failed;
      this.emit();
    }
    // Typed meanwhile, with its pause over: that's next. A failure waits for
    // the tech's next change or field left.
    if (!failed && entry.timer === null) {
      this.run(blockId, entry);
    }
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const DraftsContext = createContext<DraftStore | null>(null);

/** The session's drafts; a fresh, unshared store that saves nothing outside the provider (tests). */
export function useDrafts(): DraftStore {
  const store = useContext(DraftsContext);
  const [own] = useState(() => new DraftStore());
  return store ?? own;
}
